import { iconDisabledColor } from "template-edit/components/tools-panel";
import Shape from "shape-entities/types/shape";
import { DEFAULT_FONT_WEIGHT, OVERFLOW_KEEP_AS_IS, OVERFLOW_LAYERSIZE } from "shape-entities/render-text";
import { CSSProperties } from "react";
import { getStringWidthAndHeight } from "svg-text-utils/SVGTextWrapped";
import { Size, SizeType } from "app/types/model-types";

const { pow, round, abs } = Math

export function isJson(str): boolean {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function capitalize(str: string): string {
    return typeof str === 'string' ? str.substring(0, 1).toUpperCase() + str.substring(1) : str
}

export function isNumeric(num): boolean {
    // Check if the value can be parsed as a floating-point number and is finite.
    return !isNaN(parseFloat(num)) && isFinite(num);
}

export function getDateToday(): Date {
    return new Date(new Date().setHours(0, 0, 0, 0))
}

const toFixedFix = function (n: number, prec: number) {
    let k = pow(10, prec);
    return '' + round(n * k) / k;
};


export function number_format(v: number, decimals: number, dec_point: string, thousands_sep: string) {
    const DEFAULT_DECIMAL_SEPARATOR = ',';
    const DEFAULT_THOUSAND_SEPARATOR = '';

    let value = (v + '').replace(/[^0-9+\-Ee.]/g, '');  // Strip all characters but numerical ones.

    const n = !isFinite(+value) ? 0 : +value
    const prec = !isFinite(+decimals) ? 0 : abs(decimals)

    const sep = (typeof thousands_sep === 'undefined' || thousands_sep === null) ? DEFAULT_THOUSAND_SEPARATOR : thousands_sep
    const dec = (typeof dec_point === 'undefined' || dec_point === null) ? DEFAULT_DECIMAL_SEPARATOR : dec_point

    // Fix for IE parseFloat(0.55).toFixed(0) = 0;
    const ss = (prec ? toFixedFix(n, prec) : '' + round(n))
    const s = ss.split('.');
    if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
    }
    if ((s[1] || '').length < prec) {
        s[1] = s[1] || '';
        s[1] += new Array(prec - s[1].length + 1).join('0');
    }

    return s.join(dec);
}

export function getIconByViewTable(viewTable, iconSize = 32, iconColor = iconDisabledColor): string {
    switch (viewTable) {
        case 'diagnostics':
            return 'line-chart'
        case 'products':
            return 'cube'
        case 'validations':
            return 'image-light'
        case 'watermarks':
            return 'paw'
        case 'watermark_groups':
            return 'th' /*'grid-light'*/
        case 'images_quality':
            return 'image'
        case 'visual_similarity':
            return 'magnifying-glass-radix'
        case 'fie_queries':
            return 'code'
        default:
            return 'search'
    }
}

export function invertColor(inputHex: string): string {
    let hex: string = inputHex
    if (!hex) {
        return hex
    } else if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }

    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    if (hex.length !== 6 && hex.length !== 8) {
        console.error('invalid hex color', hex)
    }

    // invert color components
    return '#' +
        padZero((255 - parseInt(hex.slice(0, 2), 16)).toString(16)) +
        padZero((255 - parseInt(hex.slice(2, 4), 16)).toString(16)) +
        padZero((255 - parseInt(hex.slice(4, 6), 16)).toString(16))
}

function padZero(str: string, len: number = 2) {
    return (new Array(len).join('0') + str).slice(-len);
}

export function textOverflowResizeLayer(shapeObject: Shape): SizeType {
    const overflowRules = (shapeObject.text_overflow || OVERFLOW_KEEP_AS_IS)
    const textPadding = shapeObject.text_padding || 0

    const halfStrokeWidth = shapeObject.stroke_width ? .5 * shapeObject.stroke_width : 0

    const shapeWidthMinusPadding = shapeObject.width - 2 * textPadding - halfStrokeWidth
    const shapeHeightMinusPadding = (shapeObject.height || shapeObject.width) - 2 * textPadding - halfStrokeWidth

    let newWidth = shapeWidthMinusPadding
    let newHeight = shapeHeightMinusPadding

    if (overflowRules === OVERFLOW_LAYERSIZE && !shapeObject.text_wrap) {
        const textStyle: CSSProperties = {
            fontWeight: DEFAULT_FONT_WEIGHT,
            fontSize: shapeObject.text_fontsize + "px",
            fontFamily: `"${shapeObject.text_fontfamily}","Unicode-BMP-Fallback-SIL"`,
            width: shapeWidthMinusPadding + 'px',
            textDecorationColor: shapeObject.text_decoration_color || shapeObject.text_color
        }

        const size = getStringWidthAndHeight(shapeObject.text_content, { ...textStyle })

        console.debug(`debug size of text for shape ${shapeWidthMinusPadding}x${shapeHeightMinusPadding}`, size)

        const maxWidth = 4000
        const maxHeight = 4000

        if (size.w > shapeWidthMinusPadding) {
            newWidth = size.w <= maxWidth ? size.w : maxWidth
        }
        if (size.h > (shapeHeightMinusPadding || shapeWidthMinusPadding)) {
            newHeight = size.h <= maxHeight ? size.h : maxHeight
        }
    }

    return {
        width: newWidth,
        height: newHeight
    }
}